'use client'

// imports
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { useAtom } from "jotai";
import { RESET } from "jotai/utils";
import { useEffect, useState } from "react";

// lib files
import { fetchActivePassportMemberships } from "@/lib/transfer/passport";
import { PassportTransferMembership } from "@/lib/types/passportTransferMembership";
import { passportTransferMembershipAtom } from "@/lib/atoms/passport-transfer";
import { useHasMounted } from "@/lib/hooks";
import { useIdentity } from "@/lib/hooks/useIdentity";

// components
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import PassportTransferMultipleMemberships from '@/components/passport/PassportTransferMultipleMemberships/PassportTransferMultipleMemberships';
import PassportTransferNoMemberships from "@/components/passport/PassportTransferNoMemberships/PassportTransferNoMemberships";
import PassportTransferOneMembership from "@/components/passport/PassportTransferOneMembership/PassportTransferOneMembership";
import PassportTransferSignedOut from "@/components/passport/PassportTransferSignedOut/PassportTransferSignedOut";

// styles
import styles from './PassportTransferPage.module.scss';
interface PassportTransferPageProps {
  showErrorMessage?: boolean;
}

export default function PassportTransferPage(props: PassportTransferPageProps) {
  const { showErrorMessage = false } = props;
  const { identity, isAuthenticated, isResolving: isLoadingProfile } = useIdentity()
  const [isError, setIsError] = useState(showErrorMessage);
  const [activeMemberships, setActiveMemberships] = useState<null | PassportTransferMembership[]>(null);
  const [passportTransferMembership, setPassportTransferMembership ] = useAtom(passportTransferMembershipAtom);

  const hasMounted = useHasMounted();

  // Gated on a confirmed identity because transferring a membership needs a real
  // uid. Sourcing both from `identity` keeps isLoggedIn from being true while uid
  // is missing, which would leave the memberships fetch un-run and the spinner up.
  const isLoggedIn = isAuthenticated;
  const isLoading = isLoadingProfile || (isLoggedIn && activeMemberships === null);

  const signedInEmail = identity?.email;
  const uid = identity?.uid;

  useEffect(() => {
    if (!hasMounted) return;
    if (passportTransferMembership !== undefined) {
      // if the user ends up back on this page with a membership in session storage, clear it
      setPassportTransferMembership(RESET);
    }
    // Only run this effect once the component has mounted, and not when the passportTransferMembership
    // gets set, otherwise it will cause the passportMembership to reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMounted]);

  useEffect(() => {
    if (!uid) return;
    const getActivePassportMemberhips = async () => {
      try {
        const memberships = await fetchActivePassportMemberships(uid);
        setActiveMemberships(memberships);
      } catch (error) {
        console.error(error);
        setIsError(true);
      }
    };
    getActivePassportMemberhips();
  }, [uid]);


  if (!hasMounted){
    return null
  }

  let content = null;

  switch (true) {
    case isLoading:
      content = <LoadingSpinner className={styles.passport_transfer_page__loading} />;
      break;
    case !isLoggedIn:
      content = <PassportTransferSignedOut />;
      break;
    case isLoggedIn && activeMemberships && activeMemberships.length === 0 && signedInEmail && signedInEmail.length > 0:
      content = <PassportTransferNoMemberships email={signedInEmail} />;
      break;
    case isLoggedIn && activeMemberships && activeMemberships.length === 1 && signedInEmail && signedInEmail.length > 0:
      content = <PassportTransferOneMembership membership={activeMemberships[0]} email={signedInEmail} />;
      break;
    case isLoggedIn && activeMemberships && activeMemberships.length > 1 && signedInEmail && signedInEmail.length > 0:
      content = <PassportTransferMultipleMemberships memberships={activeMemberships} email={signedInEmail} />;
      break;
    default:
      break;
  }

  return (
    <div className={styles.passport_transfer_page} data-sentry-component="PassportTransferPage" data-sentry-source-file="PassportTransferPage.tsx">
      { isError && (
        <div className={styles.passport_transfer_page__error_message}>
          {"We're sorry, something went wrong."}<br/>
          {"Please sign in and try again."}
        </div>
      )}
      {content}
    </div>
  )
}
